import { DEFAULT_CRM_PAGE, isCrmPage, sameTarget, type ConsoleToCrm, type CrmTarget } from './crmBridge';
import { rendersInReact, type ReactPageGate } from './crmRail';

/** The shell's URL grammar for the two views of an account (ADR 0020). */

/** /account/:id/crm/:page? is the CRM route. */
export function parseCrmRoute(pathname: string): CrmTarget | null {
  const m = pathname.match(/^\/account\/([^/]+)\/crm(?:\/([^/]+))?\/?$/);
  if (!m) return null;
  const page = m[2];
  return { accountId: decodeURIComponent(m[1] ?? ''), page: isCrmPage(page) ? page : DEFAULT_CRM_PAGE };
}

/** The account of an AI-view account route (/account/:id, its settings, setup...). */
export function parseAiAccountRoute(pathname: string): string | null {
  const m = pathname.match(/^\/account\/([^/]+)(?:\/([^/]+))?/);
  if (!m || m[2] === 'crm') return null;
  return decodeURIComponent(m[1] ?? '');
}

/**
 * Where the console URL should follow when the frame reports a page of its own
 * (navigation inside the frame), or null to stay put. A React page on screen
 * (ADR 0023) owns the URL: the hidden frame's reports never move it.
 * `platform` is the current account's (a Fansly account is always the frame).
 */
export function frameLocationToFollow(
  current: CrmTarget | null,
  reported: { accountId?: string; page: string },
  crmIds: ReadonlySet<string>,
  platform?: string | null,
  gate: ReactPageGate = {},
): CrmTarget | null {
  if (!current || rendersInReact(current.page, platform, gate)) return null;
  if (!reported.accountId || !crmIds.has(reported.accountId) || !isCrmPage(reported.page)) return null;
  const followed: CrmTarget = { accountId: reported.accountId, page: reported.page };
  return sameTarget(current, followed) ? null : followed;
}

/** A chat the console asked the frame to open (Collections' Message). */
export interface PendingFanChat {
  accountId: string;
  fanId: string;
}

/**
 * The crm:navigate that brings the frame to the route's target, or null when
 * there is nothing to send: React pages leave the frame where it is, and a
 * target already sent is not resent, unless a fan's chat is waiting to open.
 */
export function navigateMessage(
  target: CrmTarget,
  sent: CrmTarget | null,
  pendingFan: PendingFanChat | null,
  platform?: string | null,
  gate: ReactPageGate = {},
): ConsoleToCrm | null {
  if (rendersInReact(target.page, platform, gate)) return null;
  const fanId = pendingFan && target.page === 'chats' && pendingFan.accountId === target.accountId ? pendingFan.fanId : undefined;
  if (!fanId && sameTarget(sent, target)) return null;
  return { type: 'crm:navigate', accountId: target.accountId, page: target.page, ...(fanId ? { fanId } : {}) };
}
